import React, { useCallback, type FC, type KeyboardEvent } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { AIContainer, AiFooter } from '@atlassian/jira-atlassian-intelligence';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueBreakdown } from '../../context';
import { Header } from '../common/header';
import messages from './messages';

const MAX_RETRY = 2;
type Props = {
	onRetry(issueTypeIds: string[]): void;
	onCancel(): void;
	issueHierarchyLevel?: number | undefined;
};

export const IssueBreakdownError: FC<Props> = ({ onRetry, onCancel, issueHierarchyLevel }) => {
	const { formatMessage } = useIntl();
	const [{ issueTypeIds, headerPromptValue, retryAttempts }, { incrementRetryCount }] =
		useIssueBreakdown();

	const handleRetry = useCallback(() => {
		onRetry(issueTypeIds);
		incrementRetryCount();
	}, [incrementRetryCount, issueTypeIds, onRetry]);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation(); // Prevents issue modal from closing
				onCancel();
			}
		},
		[onCancel],
	);

	const shouldShowRetry = retryAttempts < MAX_RETRY;

	return (
		<Box onKeyDown={handleKeyDown}>
			<AIContainer
				shouldFitContainer
				isShadow
				spacing="none"
				testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-error.ai-container"
			>
				<Header value={headerPromptValue} issueHierarchyLevel={issueHierarchyLevel} />
				<Box xcss={errorTextContainerStyles}>{formatMessage(messages.genericErrorMessage)}</Box>
				<FooterSection>
					<FooterSectionSpacer />
					<Button
						testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-error.cancel-esc-button"
						type="button"
						onClick={onCancel}
						interactionName="auto-generated.issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-prompt.cancel-esc-button.button"
					>
						{formatMessage(messages.labelCancel)}
						<EscText>{formatMessage(messages.labelEsc)}</EscText>
					</Button>
					{shouldShowRetry && (
						<Button
							testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-error.retry-button"
							type="button"
							onClick={handleRetry}
							appearance="primary"
							interactionName="auto-generated.issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.issue-breakdown-prompt.cancel-esc-button.button"
						>
							{formatMessage(messages.labelRetry)}
						</Button>
					)}
				</FooterSection>
				<AiFooter />
			</AIContainer>
		</Box>
	);
};

const errorTextContainerStyles = xcss({
	paddingTop: 'space.300',
	paddingLeft: 'space.200',
	paddingBottom: 'space.300',
	paddingRight: 'space.200',
	gap: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterSection = styled.div<{ hasBorder?: boolean }>(
	{
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		padding: token('space.150', '12px'),
		gap: token('space.150', '12px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ hasBorder = false }) =>
		hasBorder
			? {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					borderTop: `1px solid ${token('color.background.accent.gray.subtler', colors.N40)}`,
				}
			: {},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterSectionSpacer = styled.div({
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EscText = styled.span({
	borderRadius: token('border.radius.100', '3px'),
	padding: token('space.050', '4px'),
	color: token('color.text.subtle', '#44546F'),
	background: token('color.background.neutral', '#091E420F'),
	fontWeight: '400',
	marginLeft: token('space.075', '6px'),
});
